import {
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from 'react';
import { AuthContext } from 'Contexts/AuthContext';
import { Severity } from 'Contexts/RestaurantContext';
import { Table, Reservation } from 'types/reservations';
import { ReservationShift } from 'types/shifts';
import { stitchShifts, timeToBlock } from 'utils/helper';
import applyTableBlockChanges, {
  removeTableBlock,
  toggleFullDayTableBlock,
} from 'utils/applyTableBlockChanges';
import {
  tableBlockCoversWindow,
  tableScheduleBands,
} from 'utils/tableBlocks';
import { TableBlockModalProps } from 'App/ReservationBook/Components/TableBlockModal';
import useRestaurant from 'CustomHooks/useRestaurant';
import useToast from 'CustomHooks/useToast';
import usePinAuth from 'CustomHooks/usePinAuth';

const useTableSideBar = (
  currentTable: string | null,
  tables: Table[],
  reservations: Reservation[],
  currentShift: string | null,
  shifts: null | ReservationShift[],
  currentDate: string,
  restaurantId: string
) => {
  const { uid, DateNow } = useContext(AuthContext);
  const { experimentalFlags } = useRestaurant();
  const toast = useToast();
  const { authenticate } = usePinAuth();
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const writing = useRef(false);

  // The screen owns this state, so a table switch without onClose keeps it.
  useEffect(() => setBlockModalOpen(false), [currentTable]);

  const perShiftBlocking = !!experimentalFlags?.data?.blockPerShift;

  const table = useMemo(
    () => tables?.find((t) => t.id === currentTable) || null,
    [currentTable, tables]
  );

  const filteredReservations = useMemo(
    () => reservations.filter((res) => res.tables?.includes(table?.name || '')),
    [table, reservations]
  );

  const blocks = useMemo(
    () => filteredReservations.filter((r) => r.blocked),
    [filteredReservations]
  );

  // Stable reference: `shifts || []` inline would mint a new array per render
  // and reset the block dialog's selection through its content-key effect.
  const safeShifts = useMemo(() => shifts || [], [shifts]);

  const shift = useMemo(
    () => safeShifts.find((s) => s.id === currentShift) ?? null,
    [safeShifts, currentShift]
  );

  const [start, close, rawStart, rawClose, shiftClose] = useMemo(() => {
    let { startStop } = stitchShifts(safeShifts, currentShift);

    let start = startStop[0] || 0;
    let rawClose = startStop[1] || 0;
    // stitchShifts caps a shift that another one overlaps at its last booking
    // time; the sidebar shows the shift to its real end, like the table plan.
    const shiftClose =
      shift && Number.isFinite(shift.close)
        ? Math.max(rawClose, shift.close)
        : rawClose;
    let close = rawClose;

    // The schedule keeps at least an hour of a card cut at the closing time,
    // and the axis has to reach that far, or the card ends below its last row.
    if (shift) {
      for (const r of filteredReservations) {
        if (r.blocked || r.done || r.status === 'failed') continue;
        const from = r.startTimeInBlocks ?? timeToBlock(r.time);
        const till = r.endTimeInBlocks ?? from + (r.reservationLength ?? 0);
        if (!Number.isFinite(from) || !Number.isFinite(till)) continue;
        if (from > shiftClose || till <= rawClose) continue;
        close = Math.max(close, Math.min(till, from + 4));
      }
    }

    // Rounded values feed the schedule axis; the raw ones feed the coverage
    // check, where the rounded window would reach into a neighbouring shift.
    return [
      start - (start % 2),
      close + (close % 2),
      start,
      rawClose,
      shiftClose,
    ];
  }, [safeShifts, currentShift, shift, filteredReservations]);

  const shiftWindow = useMemo<[number, number] | null>(
    () => (shift && shiftClose > 0 ? [rawStart, shiftClose] : null),
    [shift, rawStart, shiftClose]
  );

  // A block only counts for the shift the user is currently looking at.
  const activeBlocks = useMemo(
    () => blocks.filter((r) => tableBlockCoversWindow(r, rawStart, rawClose)),
    [blocks, rawStart, rawClose]
  );

  const blocked = activeBlocks.length > 0;

  const scheduleBands = useMemo(
    () =>
      table
        ? tableScheduleBands({
            blocks,
            shifts: safeShifts,
            table,
            includeSpace: perShiftBlocking,
            start,
            close,
          })
        : [],
    [table, blocks, safeShifts, perShiftBlocking, start, close]
  );

  // Per block, so a second X on another band still goes through while the
  // first delete runs.
  const removing = useRef(new Set<string>());

  const onRemoveBlock = useCallback(
    async (blockId: string) => {
      if (removing.current.has(blockId)) return;
      removing.current.add(blockId);

      try {
        await removeTableBlock(blockId);
      } catch (e) {
        console.error('block remove', e);
        toast('Sperre konnte nicht aufgehoben werden.', Severity.ERROR);
      } finally {
        removing.current.delete(blockId);
      }
    },
    [toast]
  );

  const onSubmitBlockModal: TableBlockModalProps['onSubmit'] = useCallback(
    async ({ fullDay, shiftIds, removeOtherIds }) => {
      if (!table) return;

      try {
        await applyTableBlockChanges({
          fullDay,
          shiftIds,
          removeOtherIds,
          blocks,
          shifts: safeShifts,
          restaurantId,
          date: currentDate,
          table,
          uid: uid || '',
          now: DateNow(),
        });
      } catch (e) {
        console.error('block save', e);
        toast('Sperre konnte nicht gespeichert werden.', Severity.ERROR);
      }
    },
    [table, blocks, safeShifts, restaurantId, currentDate, uid, DateNow, toast]
  );

  // The lock always means the whole day, with or without the flag. Per-shift
  // blocking is its own button, so one control never carries two meanings.
  const onToggleBlock = useCallback(async () => {
    if (!table) return;

    // A second click before the snapshot comes back would still see the old
    // state and write a second block document.
    if (writing.current) return;
    writing.current = true;

    try {
      await toggleFullDayTableBlock({
        blocked,
        // Unblocking lifts what the current shift shows, a block on another
        // shift of the day stays. Blocking replaces every block anyway.
        blocks: blocked ? activeBlocks : blocks,
        restaurantId,
        date: currentDate,
        table,
        uid: uid || '',
        now: DateNow(),
      });
    } catch (e) {
      console.error('block toggle', e);
      toast('Sperre konnte nicht gespeichert werden.', Severity.ERROR);
    } finally {
      writing.current = false;
    }
  }, [
    table,
    blocked,
    activeBlocks,
    blocks,
    restaurantId,
    currentDate,
    uid,
    DateNow,
    toast,
  ]);

  return {
    blocked,
    scheduleBands,
    // Removing a single block is new operation, so it follows the flag.
    onRemoveBlock: perShiftBlocking
      ? (blockId: string) =>
          authenticate('reservations.tableBlocked', () =>
            onRemoveBlock(blockId)
          )
      : undefined,
    reservations: filteredReservations,
    name: table?.name || '',
    id: currentTable || '',
    table,
    start,
    close,
    shiftWindow,
    onToggleBlock: () =>
      authenticate('reservations.tableBlocked', onToggleBlock),
    perShiftBlocking,
    onOpenBlockModal: () => {
      if (table)
        authenticate('reservations.tableBlocked', () =>
          setBlockModalOpen(true)
        );
    },
    blockModal: {
      open: blockModalOpen,
      tableName: table?.name || '',
      table,
      shifts: safeShifts,
      blocks,
      defaultShiftId: currentShift,
      onClose: () => setBlockModalOpen(false),
      onSubmit: onSubmitBlockModal,
    },
  };
};

export default useTableSideBar;
